import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Car, UserCog, AlertTriangle, ShieldCheck, Wrench, Clock, ArrowRight, TrendingUp, ShieldAlert, IdCard, FileWarning } from "lucide-react";

const badge = "inline-block bg-white/60 border border-[#e5e0d8] text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold px-2 py-0.5 rounded-sm";

function Stat({ icon: Icon, label, value, color, testid }) {
  return (
    <div className={`bg-white rounded-sm border border-[#e5e0d8] p-5 flex items-start gap-4`} data-testid={testid}>
      <div className={`w-11 h-11 rounded-sm flex items-center justify-center ${color}`}>
        <Icon size={20} className="text-white" strokeWidth={1.6} />
      </div>
      <div className="flex-1">
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">{label}</div>
        <div className="font-serif text-3xl text-[#1c1a17] mt-1">{value}</div>
      </div>
    </div>
  );
}

function severityPill(sev) {
  if (sev === "expired") return { label: "Expired", cls: "bg-red-100 text-red-800 border-red-300" };
  if (sev === "critical") return { label: "≤ 30 days", cls: "bg-orange-100 text-orange-800 border-orange-300" };
  return { label: "≤ 90 days", cls: "bg-yellow-100 text-yellow-800 border-yellow-300" };
}

function AlertList({ icon: Icon, title, items, kind, testid }) {
  return (
    <div className="p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className="text-[#B22222]" />
        <div className="font-serif text-lg text-[#1c1a17]">{title}</div>
        <span className="ml-auto text-[10px] tracking-widest uppercase text-[#6b655b] font-semibold">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-[#8a8478] py-4">All clear.</div>
      ) : (
        <ul className="space-y-2 max-h-64 overflow-y-auto pr-1">
          {items.slice(0, 15).map((it, i) => {
            const pill = severityPill(it.severity);
            const label = kind === "license" ? it.name : `${it.registration_number}${it.category ? " (" + it.category + ")" : ""}`;
            const sub = kind === "license"
              ? `${it.license_number || "no licence #"}${it.license_class ? " · " + it.license_class : ""}`
              : (it.doc_type || "Document");
            return (
              <li key={i} data-testid={`${testid}-item-${i}`} className="bg-[#faf8f2] rounded-sm p-2.5 flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#1c1a17] truncate">{label}</div>
                  <div className="text-[11px] text-[#8a8478] truncate">{sub}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs font-semibold text-[#1c1a17] tabular-nums">{it.expiry_date}</div>
                  <div className={`inline-block mt-0.5 text-[10px] tracking-widest uppercase font-semibold px-1.5 py-0.5 rounded-sm border ${pill.cls}`}>{pill.label}</div>
                </div>
              </li>
            );
          })}
          {items.length > 15 && (
            <li className="text-[11px] text-[#8a8478] text-center pt-1">+ {items.length - 15} more</li>
          )}
        </ul>
      )}
    </div>
  );
}

function ListPanel({ title, rows, emptyMsg, columns, testid }) {  return (
    <div className="bg-white rounded-sm border border-[#e5e0d8]" data-testid={testid}>
      <div className="px-5 py-4 border-b border-[#e5e0d8] flex items-baseline justify-between gap-3">
        <div className="font-serif text-lg text-[#1c1a17]">{title}</div>
        <span className={badge}>{rows.length} record{rows.length === 1 ? "" : "s"}</span>
      </div>
      {rows.length === 0 ? (
        <div className="px-5 py-8 text-sm text-[#6b655b] text-center">{emptyMsg}</div>
      ) : (
        <div className="divide-y divide-[#f0ece2]">
          {rows.slice(0, 6).map((r, i) => (
            <div key={i} className="px-5 py-3 grid grid-cols-3 gap-3 text-sm items-center">
              {columns.map((c) => (
                <div key={c.key} className={c.className || "text-[#1c1a17]"}>
                  {c.render ? c.render(r) : (r[c.key] || <span className="text-[#a09789]">—</span>)}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function VehicleAdminDashboard() {
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/vehicles-summary");
        setSummary(data);
      } catch (e) {
        setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load fleet summary");
      }
    })();
  }, []);

  const v = summary?.vehicles || { total: 0, by_status: {}, documents_expired: [], documents_expiring_30d: [] };
  const d = summary?.drivers || { total: 0, by_status: {}, licenses_expired: [], licenses_expiring_30d: [] };
  const c = summary?.costs || { total_purchase_cost: 0, total_service_cost: 0, total_cost_of_ownership: 0, top_spend_vehicles: [] };
  const alerts = summary?.important_alerts || { total: 0, horizon_days: 90, insurance: [], fitness: [], licenses: [] };
  const fmtInr = (n) => `₹ ${Math.round(Number(n || 0)).toLocaleString("en-IN")}`;

  return (
    <div className="space-y-6" data-testid="vehicle-admin-dashboard">
      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Vehicle Maintenance</div>
        <h1 className="font-serif text-3xl text-[#1c1a17]">Fleet Dashboard</h1>
        <div className="text-sm text-[#6b655b] mt-0.5">Vehicles, drivers, and compliance at a glance.</div>
      </div>

      {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-sm">{err}</div>}

      {alerts.total > 0 && (
        <div className="bg-white border-l-4 border-l-[#B22222] border border-[#e5e0d8] rounded-sm overflow-hidden" data-testid="important-alerts">
          <div className="bg-gradient-to-r from-[#B22222]/10 to-transparent px-5 py-3 border-b border-[#e5e0d8] flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-[#B22222] text-white flex items-center justify-center animate-pulse"><AlertTriangle size={18} /></div>
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#B22222] font-bold">Important Alert</div>
                <div className="font-serif text-xl text-[#1c1a17]">
                  {alerts.total} compliance item{alerts.total === 1 ? "" : "s"} expiring within {alerts.horizon_days} days
                </div>
              </div>
            </div>
            <div className="flex gap-4 text-[11px] text-[#6b655b] font-semibold">
              <span data-testid="alerts-count-insurance"><span className="text-[#B22222] font-bold">{alerts.insurance.length}</span> Insurance</span>
              <span data-testid="alerts-count-fitness"><span className="text-[#B22222] font-bold">{alerts.fitness.length}</span> Fitness</span>
              <span data-testid="alerts-count-licenses"><span className="text-[#B22222] font-bold">{alerts.licenses.length}</span> Licences</span>
            </div>
          </div>
          <div className="grid lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-[#f0ece2]">
            <AlertList icon={ShieldAlert} title="Insurance" items={alerts.insurance} kind="doc" testid="alert-list-insurance" />
            <AlertList icon={FileWarning} title="Fitness Certificate" items={alerts.fitness} kind="doc" testid="alert-list-fitness" />
            <AlertList icon={IdCard} title="Driving Licence" items={alerts.licenses} kind="license" testid="alert-list-licenses" />
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Car} label="Total Vehicles" value={v.total} color="bg-[#234333]" testid="stat-vehicles-total" />
        <Stat icon={Wrench} label="Under Repair" value={v.by_status?.["Under Repair"] || 0} color="bg-[#B8860B]" testid="stat-under-repair" />
        <Stat icon={AlertTriangle} label="Breakdown" value={v.by_status?.["Breakdown"] || 0} color="bg-[#B22222]" testid="stat-breakdown" />
        <Stat icon={UserCog} label="Total Drivers" value={d.total} color="bg-[#0D5C63]" testid="stat-drivers-total" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-sm border border-[#e5e0d8] p-5" data-testid="tile-tco-purchase">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Total Purchase Cost</div>
          <div className="font-serif text-2xl text-[#1c1a17] mt-1">{fmtInr(c.total_purchase_cost)}</div>
        </div>
        <div className="bg-white rounded-sm border border-[#e5e0d8] p-5" data-testid="tile-tco-service">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Total Service Spend</div>
          <div className="font-serif text-2xl text-[#B8860B] mt-1">{fmtInr(c.total_service_cost)}</div>
        </div>
        <div className="bg-[#234333] text-white rounded-sm p-5" data-testid="tile-tco-total">
          <div className="text-[10px] tracking-[0.25em] uppercase text-white/70 font-semibold flex items-center gap-1"><TrendingUp size={11} />Fleet Cost of Ownership</div>
          <div className="font-serif text-2xl mt-1">{fmtInr(c.total_cost_of_ownership)}</div>
        </div>
      </div>

      {(c.top_spend_vehicles || []).length > 0 && (
        <div className="bg-white rounded-sm border border-[#e5e0d8]" data-testid="panel-top-spend">
          <div className="px-5 py-4 border-b border-[#e5e0d8] flex items-baseline justify-between gap-3">
            <div className="font-serif text-lg text-[#1c1a17]">Top Spend Vehicles</div>
            <span className={badge}>{(c.top_spend_vehicles || []).length} shown</span>
          </div>
          <div className="divide-y divide-[#f0ece2]">
            {(c.top_spend_vehicles || []).map((row) => (
              <div key={row.vehicle_id} className="px-5 py-3 grid grid-cols-4 gap-3 text-sm items-center" data-testid={`top-spend-row-${row.vehicle_id}`}>
                <div className="font-semibold text-[#1c1a17]">{row.registration_number}{row.category ? ` (${row.category})` : ""}</div>
                <div className="text-[#5b5650]">{row.make} {row.model_name}</div>
                <div className="text-right tabular-nums text-[#B8860B] font-semibold">{fmtInr(row.service_cost)}</div>
                <div className="text-right tabular-nums text-[#1c1a17]">{fmtInr(row.total_cost_of_ownership)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <ListPanel
          title="Vehicle Documents — Expiring in 30 days"
          rows={v.documents_expiring_30d || []}
          emptyMsg="No documents expiring soon."
          columns={[
            { key: "registration_number", className: "font-semibold text-[#1c1a17]", render: (r) => `${r.registration_number}${r.category ? ` (${r.category})` : ""}` },
            { key: "doc_type" },
            { key: "expiry_date", className: "text-[#B8860B] font-semibold text-right" },
          ]}
          testid="panel-veh-docs-expiring"
        />
        <ListPanel
          title="Vehicle Documents — Already Expired"
          rows={v.documents_expired || []}
          emptyMsg="Nothing expired. Nicely done."
          columns={[
            { key: "registration_number", className: "font-semibold text-[#1c1a17]", render: (r) => `${r.registration_number}${r.category ? ` (${r.category})` : ""}` },
            { key: "doc_type" },
            { key: "expiry_date", className: "text-[#B22222] font-semibold text-right" },
          ]}
          testid="panel-veh-docs-expired"
        />
        <ListPanel
          title="Driving Licences — Expiring in 30 days"
          rows={d.licenses_expiring_30d || []}
          emptyMsg="All licences valid for the next month."
          columns={[
            { key: "name", className: "font-semibold text-[#1c1a17]" },
            { key: "license_number" },
            { key: "expiry_date", className: "text-[#B8860B] font-semibold text-right" },
          ]}
          testid="panel-lic-expiring"
        />
        <ListPanel
          title="Driving Licences — Already Expired"
          rows={d.licenses_expired || []}
          emptyMsg="No expired licences."
          columns={[
            { key: "name", className: "font-semibold text-[#1c1a17]" },
            { key: "license_number" },
            { key: "expiry_date", className: "text-[#B22222] font-semibold text-right" },
          ]}
          testid="panel-lic-expired"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Link to="/admin/vehicles" data-testid="quick-link-vehicles" className="group bg-[#234333] text-white p-6 rounded-sm flex items-center justify-between hover:bg-[#1a3326] transition-colors">
          <div>
            <ShieldCheck size={22} className="opacity-70" />
            <div className="mt-2 font-serif text-2xl">Vehicle Master</div>
            <div className="text-white/70 text-xs uppercase tracking-widest mt-1">Add, edit, retire vehicles</div>
          </div>
          <ArrowRight className="opacity-60 group-hover:translate-x-1 transition-transform" />
        </Link>
        <Link to="/admin/drivers" data-testid="quick-link-drivers" className="group bg-[#0D5C63] text-white p-6 rounded-sm flex items-center justify-between hover:bg-[#083E43] transition-colors">
          <div>
            <Clock size={22} className="opacity-70" />
            <div className="mt-2 font-serif text-2xl">Driver Management</div>
            <div className="text-white/70 text-xs uppercase tracking-widest mt-1">Licences · Training · Medical · Police</div>
          </div>
          <ArrowRight className="opacity-60 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </div>
  );
}
